import React from 'react'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'
import { get } from 'lodash'
import { useQuery } from '@apollo/client'
import { ThemeProvider } from '@material-ui/core/styles'
import BackButton from 'components/back-button'
import Error from 'components/error'
import { useAuth } from 'lib/auth'
import themes, { extendTheme } from 'themes'
import {
  MobileBookingSummary,
  getPriceLabel,
} from 'pages/schedule/SelectLessonTime/redesign/booking-summary-card'
import useChromeStyles, {
  PAGE_BACKGROUND,
} from 'pages/schedule/SelectLessonTime/redesign/styles'
import SchedulePageBackground from 'pages/schedule/components/page-background'
import CancellationCallout from 'pages/schedule/components/cancellation-callout'
import { GetStudentsQuery } from '../query'
import AccountRequiredPanel from './account-required-panel'
import CoachStudentForm from './coach-student-form'
import ConsumerStudentForm from './consumer-student-form'
import { useIsCompact } from './form-parts'
import GuestDetailsForm from './guest-details-form'
import { FormCardSkeleton } from './select-student-skeleton-redesign'
import StudentSummaryCard from './student-summary-card'
import useStyles from './styles'
import YourTimeCard from './your-time-card'
import {
  buildEditTimeUrl,
  buildSelectStudentEvent,
  formatSlotRange,
} from './utils'

const refreshTheme = extendTheme(themes.offeringsRefresh)

// Signed-in golfers and coaches load their students before the form shows.
const SignedInForm = ({
  isCoach,
  user,
  currentUrl,
  eventObject,
  submitLabel,
  priceLabel,
  belowCard,
}) => {
  const { data, loading, error } = useQuery(GetStudentsQuery)

  if (loading) return <FormCardSkeleton />
  if (error) return <Error error={error} />

  const students = get(data, 'currentUser.students')
  const Form = isCoach ? CoachStudentForm : ConsumerStudentForm
  return (
    <Form
      students={students}
      user={user}
      currentUrl={currentUrl}
      eventObject={eventObject}
      submitLabel={submitLabel}
      priceLabel={priceLabel}
      belowCard={belowCard}
    />
  )
}

SignedInForm.propTypes = {
  isCoach: PropTypes.bool,
  user: PropTypes.object,
  currentUrl: PropTypes.string.isRequired,
  eventObject: PropTypes.object,
  submitLabel: PropTypes.string.isRequired,
  priceLabel: PropTypes.string,
  belowCard: PropTypes.node,
}

// Redesigned "Who's taking the lesson?" step of private lesson booking, shown
// when FLAG_FEAT_PRIVATE_BOOKING_REDESIGN is on. The legacy layout lives in
// ../SelectStudentLayout.js and friends. Page chrome, summary card and mobile
// pattern match the step 1 redesign.
const SelectStudentContent = ({
  currentUrl,
  previousUrl,
  lessonType,
  lessonCreditBalanceQuantity,
}) => {
  const chrome = useChromeStyles()
  const classes = useStyles()
  const isCompact = useIsCompact()
  const location = useLocation()
  const { isLoggedIn, isCoach, user } = useAuth()

  const coach = lessonType.coach
  const timezone = get(lessonType, ['facilities', '0', 'timezone'])
  const { startDateTime } = qs.parse(location.search)
  const slotLabel = formatSlotRange(
    startDateTime,
    lessonType.durationInMinutes,
    timezone,
  )
  const editUrl = buildEditTimeUrl(previousUrl, startDateTime, timezone)
  const isLessonPack = (lessonType.lessonPacks || []).length > 0
  const showGuestForm = !isLoggedIn && !isLessonPack
  const priceLabel = getPriceLabel(lessonType)
  // Free lessons and lesson credit redemptions skip payment.
  const submitLabel =
    priceLabel === 'Free' || lessonCreditBalanceQuantity
      ? 'Continue'
      : 'Continue to Payment'
  const eventObject = buildSelectStudentEvent(lessonType, startDateTime)

  const mobileCallout = isCompact ? (
    <CancellationCallout
      canStudentCancel={coach.canStudentCancel}
      cancellationPolicy={coach.cancellationPolicy}
      className={classes.mobileCallout}
    />
  ) : null

  const form = (() => {
    if (isLoggedIn) {
      return (
        <SignedInForm
          isCoach={isCoach}
          user={user}
          currentUrl={currentUrl}
          eventObject={eventObject}
          submitLabel={submitLabel}
          priceLabel={priceLabel}
          belowCard={mobileCallout}
        />
      )
    }
    if (showGuestForm) {
      return (
        <GuestDetailsForm
          currentUrl={currentUrl}
          submitLabel={submitLabel}
          priceLabel={priceLabel}
          belowCard={mobileCallout}
        />
      )
    }
    return <AccountRequiredPanel />
  })()

  return (
    <SchedulePageBackground color={PAGE_BACKGROUND} className={chrome.page}>
      <div className={chrome.inner}>
        {isCompact ? (
          <div className={chrome.mobileTopCard}>
            <div className={chrome.mobileBackRow}>
              <BackButton previousUrl={previousUrl} />
            </div>
            <MobileBookingSummary lessonType={lessonType} coach={coach} />
          </div>
        ) : (
          <div className={chrome.desktopBack}>
            <BackButton previousUrl={previousUrl} />
          </div>
        )}
        <span className={classes.srOnly}>Step 2 of 3</span>
        <div className={chrome.grid}>
          <div className={`${chrome.mainCol} ${classes.stack}`}>
            <YourTimeCard slotLabel={slotLabel} editUrl={editUrl} />
            {form}
          </div>
          {!isCompact && (
            <div className={chrome.sideCol}>
              <StudentSummaryCard
                lessonType={lessonType}
                coach={coach}
                slotLabel={slotLabel}
              />
            </div>
          )}
        </div>
      </div>
    </SchedulePageBackground>
  )
}

SelectStudentContent.propTypes = {
  currentUrl: PropTypes.string.isRequired,
  previousUrl: PropTypes.string.isRequired,
  lessonType: PropTypes.object.isRequired,
  lessonCreditBalanceQuantity: PropTypes.string,
}

const SelectStudentRedesign = props => (
  <ThemeProvider theme={refreshTheme}>
    <SelectStudentContent {...props} />
  </ThemeProvider>
)

export default SelectStudentRedesign
